import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { UserPlus, Trash2, ShieldCheck, Camera, Upload, X, Pencil, Star, StarOff, ScanFace } from "lucide-react";
import { PageHeader, TableCard } from "@/components/PageHeader";
import { loadFaceModels, detectFaceDescriptor } from "@/lib/faceapi";

// Extrai o embedding facial a partir de uma data URL de imagem.
// Retorna null se não detectar rosto (foto continua sendo salva, mas sem acesso catraca).
async function faceEmbeddingFromDataUrl(dataUrl) {
  try {
    await loadFaceModels();
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = dataUrl;
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
    const desc = await detectFaceDescriptor(img);
    return desc?.descriptor?.length === 128 ? desc.descriptor : null;
  } catch (e) {
    console.warn("[user-face] falhou:", e);
    return null;
  }
}

const MODULES = [
  { key: "/", label: "Dashboard" },
  { key: "/consolidado", label: "Consolidado (admin)" },
  { key: "/venda-rapida", label: "Venda Rápida" },
  { key: "/alunos", label: "Alunos" },
  { key: "/planos", label: "Planos" },
  { key: "/treinos", label: "Treinos" },
  { key: "/catraca", label: "Catraca" },
  { key: "/financeiro", label: "Financeiro" },
  { key: "/pdv", label: "Loja / PDV" },
  { key: "/mapa-alunos", label: "Mapa de Alunos" },
  { key: "/biblioteca-exercicios", label: "Biblioteca" },
  { key: "/videos-american-fit", label: "Vídeos American Fit (admin)" },
  { key: "/caixa-dia", label: "Caixa do Dia" },
  { key: "/balanco-mensal", label: "Balanço Mensal" },
  { key: "/avisos", label: "Avisos (Portal)" },
  { key: "/feedback-alunos", label: "Feedback Alunos" },
  { key: "/site-vendas", label: "Site de Vendas" },
  { key: "/relatorios", label: "Relatórios" },
  { key: "/auditoria", label: "Auditoria (admin)" },
  { key: "/seguranca", label: "Segurança (admin)" },
  { key: "/avisos-usuarios", label: "Avisos Usuários (admin)" },
  { key: "/dispositivos", label: "Dispositivos" },
  { key: "/unidades", label: "Unidades" },
  { key: "/usuarios", label: "Usuários" },
  { key: "/avaliacoes", label: "Avaliações (admin)" },
  { key: "/configuracoes", label: "Configurações (admin)" },
  { key: "/migracao-nextfit", label: "Migração Next Fit (admin)" },
];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [customRoles, setCustomRoles] = useState([]); // [{key,label}]
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "reception", unit_id: "", permissions: [], photo: "", face_embedding: null, phone: "" });
  const [permDialog, setPermDialog] = useState(null);
  const [editingPerms, setEditingPerms] = useState([]);
  const [photoDialog, setPhotoDialog] = useState(null); // user being edited
  const [photoPreview, setPhotoPreview] = useState("");
  const [photoFaceOk, setPhotoFaceOk] = useState(null); // null = não analisado, true/false = rosto detectado
  const [photoDetecting, setPhotoDetecting] = useState(false);
  const [photoSaving, setPhotoSaving] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "reception", unit_id: "", phone: "", password: "", all_units: false });
  const [editSaving, setEditSaving] = useState(false);

  const load = async () => {
    try {
      const [u, un, cr] = await Promise.all([
        api.get("/users"),
        api.get("/units"),
        api.get("/custom-roles").catch(()=>({data: []})),
      ]);
      setUsers(u.data); setUnits(un.data); setCustomRoles(cr.data || []);
    } catch { toast.error("Sem permissão"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form };
      if (!payload.unit_id) delete payload.unit_id;
      await api.post("/users", payload);
      toast.success("Usuário criado");
      setOpen(false); setForm({ name: "", email: "", password: "", role: "reception", unit_id: "", permissions: [], photo: "", face_embedding: null, phone: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const toggleActive = async (u) => { await api.patch(`/users/${u.id}`, { active: !u.active }); load(); };
  const toggleEvaluable = async (u) => {
    const next = u.evaluable === false ? true : false;
    try {
      await api.patch(`/users/${u.id}`, { evaluable: next });
      toast.success(next ? `${u.name} pode ser avaliado no portal` : `${u.name} não aparecerá para avaliação`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const remove = async (id) => { if (!window.confirm("Remover?")) return; await api.delete(`/users/${id}`); load(); };

  // Photo upload
  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({
      name: u.name || "", email: u.email || "", role: u.role || "reception",
      unit_id: u.unit_id || "", phone: u.phone || "", password: "", all_units: !!u.all_units,
    });
  };
  const saveEdit = async () => {
    if (!editForm.name.trim() || !editForm.email.trim()) return toast.error("Nome e e-mail obrigatórios");
    setEditSaving(true);
    try {
      const payload = {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        role: editForm.role,
        unit_id: editForm.role === "admin" ? null : (editForm.unit_id || null),
        phone: editForm.phone || null,
        all_units: editForm.role === "admin" ? false : editForm.all_units,
      };
      if (editForm.password) payload.password = editForm.password;
      await api.patch(`/users/${editUser.id}`, payload);
      toast.success("Usuário atualizado");
      setEditUser(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setEditSaving(false); }
  };

  const openPhoto = (u) => { setPhotoDialog(u); setPhotoPreview(u.photo || ""); setPhotoFaceOk(u.face_embedding ? true : null); };
  const handlePhotoFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 3 * 1024 * 1024) return toast.error("Imagem muito grande (máx 3MB)");
    const r = new FileReader();
    r.onload = async () => {
      setPhotoPreview(r.result);
      setPhotoFaceOk(null); setPhotoDetecting(true);
      const emb = await faceEmbeddingFromDataUrl(r.result);
      setPhotoFaceOk(!!emb);
      setPhotoDetecting(false);
      if (emb) toast.success("Rosto detectado — vai liberar na catraca após salvar");
      else toast.warning("Rosto não detectado — foto será salva, mas não vai liberar na catraca");
      // guarda temporariamente para enviar no save
      setPhotoDialog(prev => prev ? { ...prev, _pending_embedding: emb } : prev);
    };
    r.readAsDataURL(f);
  };
  const savePhoto = async () => {
    setPhotoSaving(true);
    try {
      const body = { photo: photoPreview || null };
      if (photoDialog?._pending_embedding !== undefined) body.face_embedding = photoDialog._pending_embedding;
      await api.patch(`/users/${photoDialog.id}`, body);
      toast.success(body.face_embedding ? "Foto atualizada — catraca liberada" : "Foto atualizada");
      setPhotoDialog(null); setPhotoPreview(""); setPhotoFaceOk(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setPhotoSaving(false); }
  };
  const removePhoto = async () => {
    if (!window.confirm("Remover a foto? O funcionário também perderá o acesso facial à catraca.")) return;
    setPhotoSaving(true);
    try {
      await api.patch(`/users/${photoDialog.id}`, { photo: null, face_embedding: null });
      toast.success("Foto removida");
      setPhotoDialog(null); setPhotoPreview(""); setPhotoFaceOk(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setPhotoSaving(false); }
  };

  const openPerms = (u) => { setPermDialog(u); setEditingPerms(u.permissions || []); };
  const togglePerm = (k) => setEditingPerms(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);
  const savePerms = async () => {
    try {
      await api.patch(`/users/${permDialog.id}`, { permissions: editingPerms });
      toast.success("Permissões atualizadas");
      setPermDialog(null); load();
    } catch { toast.error("Erro"); }
  };

  const roleLabel = (r) => {
    const base = { admin: "Administrador", reception: "Recepção", trainer: "Personal", finance: "Financeiro" };
    if (base[r]) return base[r];
    const cr = customRoles.find(x => x.key === r);
    return cr?.label || r;
  };
  const unitName = (id) => units.find(u => u.id === id)?.name || (id ? "—" : "Todas");

  return (
    <div className="space-y-4">
      <PageHeader
        title="Usuários"
        subtitle={`${users.length} usuários · Equipe da academia · permissões por módulo customizáveis`}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="user-add-button" className="bg-rose-600 hover:bg-rose-700 font-bold uppercase"><UserPlus className="w-4 h-4 mr-2"/>Novo usuário</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Novo usuário</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-4">
                <div><Label>Nome</Label><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}/></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})}/></div>
                <div><Label>Senha</Label><Input type="password" value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})}/></div>
                <div>
                  <Label>Foto do funcionário (opcional)</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <div className="w-16 h-16 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden shrink-0">
                      {form.photo ? <img src={form.photo} alt="" className="w-full h-full object-cover"/> : <Camera className="w-6 h-6 text-slate-400"/>}
                    </div>
                    <label className="inline-flex items-center gap-2 cursor-pointer bg-slate-900 text-white px-3 py-2 rounded-lg text-xs font-bold uppercase hover:bg-black" data-testid="user-create-photo-upload">
                      <Upload className="w-4 h-4"/>Escolher foto
                      <input type="file" accept="image/*" className="hidden" onChange={async (e)=>{
                        const f = e.target.files?.[0]; if (!f) return;
                        if (f.size > 3*1024*1024) return toast.error("Máx 3MB");
                        const r = new FileReader();
                        r.onload = async () => {
                          setForm(prev => ({ ...prev, photo: r.result }));
                          const emb = await faceEmbeddingFromDataUrl(r.result);
                          setForm(prev => ({ ...prev, face_embedding: emb }));
                          if (emb) toast.success("Rosto detectado — libera catraca");
                          else toast.warning("Rosto não detectado — foto sem acesso facial");
                        };
                        r.readAsDataURL(f);
                      }}/>
                    </label>
                    {form.photo && <Button size="sm" variant="outline" onClick={()=>setForm({...form, photo: "", face_embedding: null})}>Remover</Button>}
                  </div>
                  {form.photo && (
                    <p className="text-[11px] mt-1 flex items-center gap-1">
                      {form.face_embedding ? (
                        <span className="text-emerald-700 flex items-center gap-1"><ScanFace className="w-3 h-3"/>Rosto detectado — vai liberar na catraca</span>
                      ) : (
                        <span className="text-amber-700">Nenhum rosto detectado — foto salva, mas sem acesso facial</span>
                      )}
                    </p>
                  )}
                </div>
                <div>
                  <Label>Perfil</Label>
                  <Select value={form.role} onValueChange={(v)=>setForm({...form,role:v})}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Administrador (todas unidades)</SelectItem>
                      <SelectItem value="reception">Recepção</SelectItem>
                      <SelectItem value="trainer">Personal Trainer</SelectItem>
                      <SelectItem value="finance">Financeiro</SelectItem>
                      {customRoles.map(r => (
                        <SelectItem key={r.key} value={r.key} data-testid={`user-role-${r.key}`}>{r.label} <span className="text-[10px] text-violet-500 ml-1">(Custom)</span></SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {form.role !== "admin" && (
                  <div>
                    <Label>Unidade</Label>
                    <Select value={form.unit_id} onValueChange={(v)=>setForm({...form,unit_id:v})}>
                      <SelectTrigger><SelectValue placeholder="Selecionar"/></SelectTrigger>
                      <SelectContent>{units.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <Button onClick={save} className="w-full bg-rose-600 hover:bg-rose-700 font-bold uppercase">Salvar</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="border border-slate-200 overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Nome</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Perfil</th>
              <th className="px-4 py-3">Unidade</th><th className="px-4 py-3">Permissões</th><th className="px-4 py-3">Avaliável</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody data-testid="users-list">
            {users.map(u => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <button onClick={()=>openPhoto(u)} data-testid={`user-photo-${u.id}`} title="Alterar foto" className="relative group shrink-0">
                      {u.photo ? (
                        <img src={u.photo} alt={u.name} className="w-10 h-10 rounded-full object-cover border-2 border-slate-200 group-hover:border-rose-400 transition"/>
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center font-bold text-slate-500 border-2 border-slate-200 group-hover:border-rose-400 transition">{u.name[0]?.toUpperCase()}</div>
                      )}
                      <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-rose-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                        <Camera className="w-2.5 h-2.5"/>
                      </span>
                    </button>
                    <span className="font-medium text-slate-900">{u.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600 text-sm">{u.email}</td>
                <td className="px-4 py-3"><span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-full font-semibold">{roleLabel(u.role)}</span></td>
                <td className="px-4 py-3 text-sm text-slate-700">{u.all_units && u.role !== "admin" ? <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold" data-testid={`user-all-units-${u.id}`}>Todas as unidades</span> : unitName(u.unit_id)}</td>
                <td className="px-4 py-3">
                  <Button variant="outline" size="sm" onClick={()=>openPerms(u)} data-testid="user-perms-button" className="text-xs">
                    <ShieldCheck className="w-3 h-3 mr-1"/>{u.role === "admin" ? ((u.permissions || []).length && !u.is_owner ? `${u.permissions.length}` : "todas") : `${(u.permissions || []).length || "padrão"}`}
                  </Button>
                </td>
                <td className="px-4 py-3">
                  {u.role === "finance" ? (
                    <span className="text-[10px] text-slate-400 uppercase font-bold">N/A</span>
                  ) : (
                    <button onClick={()=>toggleEvaluable(u)} data-testid={`user-evaluable-toggle-${u.id}`}
                      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-semibold transition-colors ${u.evaluable !== false ? "bg-amber-50 text-amber-700 hover:bg-amber-100" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                      title={u.evaluable !== false ? "Aparece na aba 'Avaliar' do portal do aluno" : "Não aparece para os alunos"}>
                      {u.evaluable !== false ? <><Star className="w-3 h-3 fill-current"/>Sim</> : <><StarOff className="w-3 h-3"/>Não</>}
                    </button>
                  )}
                </td>
                <td className="px-4 py-3">
                  <button onClick={()=>toggleActive(u)} data-testid="user-status-toggle" className={`text-xs px-2 py-1 rounded-full font-semibold ${u.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {u.active ? "Ativo" : "Inativo"}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 justify-end">
                    <Button variant="ghost" size="icon" onClick={()=>openEdit(u)} data-testid={`user-edit-${u.id}`} title="Editar usuário"><Pencil className="w-4 h-4 text-sky-600"/></Button>
                    <Button variant="ghost" size="icon" onClick={()=>remove(u.id)} title="Remover"><Trash2 className="w-4 h-4 text-slate-400"/></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!editUser} onOpenChange={(v)=>!v && setEditUser(null)}>
        <DialogContent className="max-w-md" data-testid="user-edit-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-sky-600"/>Editar {editUser?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label>Nome *</Label><Input value={editForm.name} onChange={(e)=>setEditForm({...editForm, name: e.target.value})} data-testid="user-edit-name"/></div>
            <div><Label>Email *</Label><Input type="email" value={editForm.email} onChange={(e)=>setEditForm({...editForm, email: e.target.value})} data-testid="user-edit-email"/></div>
            <div><Label>Telefone (WhatsApp)</Label><Input value={editForm.phone} onChange={(e)=>setEditForm({...editForm, phone: e.target.value})} placeholder="(11) 99999-9999" data-testid="user-edit-phone"/></div>
            <div>
              <Label>Perfil</Label>
              <Select value={editForm.role} onValueChange={(v)=>setEditForm({...editForm, role: v})}>
                <SelectTrigger data-testid="user-edit-role"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador (todas unidades)</SelectItem>
                  <SelectItem value="reception">Recepção</SelectItem>
                  <SelectItem value="trainer">Personal Trainer</SelectItem>
                  <SelectItem value="finance">Financeiro</SelectItem>
                  {customRoles.map(r => (
                    <SelectItem key={r.key} value={r.key}>{r.label} <span className="text-[10px] text-violet-500 ml-1">(Custom)</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {editForm.role !== "admin" && (
              <div>
                <Label>Acesso às unidades</Label>
                <Select value={editForm.all_units ? "all" : "one"} onValueChange={(v)=>setEditForm({...editForm, all_units: v === "all"})}>
                  <SelectTrigger data-testid="user-edit-unit-access"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one" data-testid="user-edit-unit-access-one">Somente a unidade escolhida</SelectItem>
                    <SelectItem value="all" data-testid="user-edit-unit-access-all">Todas as unidades</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {editForm.role !== "admin" && (
              <div>
                <Label>{editForm.all_units ? "Unidade principal" : "Unidade"}</Label>
                <Select value={editForm.unit_id} onValueChange={(v)=>setEditForm({...editForm, unit_id: v})}>
                  <SelectTrigger data-testid="user-edit-unit"><SelectValue placeholder="Selecionar"/></SelectTrigger>
                  <SelectContent>{units.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Nova senha (opcional)</Label>
              <Input type="password" value={editForm.password} onChange={(e)=>setEditForm({...editForm, password: e.target.value})} placeholder="Deixe em branco para não alterar" data-testid="user-edit-password"/>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={()=>setEditUser(null)}>Cancelar</Button>
              <Button onClick={saveEdit} disabled={editSaving} className="bg-emerald-600 hover:bg-emerald-700 font-bold uppercase" data-testid="user-edit-save">{editSaving ? "Salvando..." : "Salvar alterações"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!photoDialog} onOpenChange={(v)=>{if(!v){ setPhotoDialog(null); setPhotoPreview(""); }}}>
        <DialogContent className="max-w-md" data-testid="user-photo-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Camera className="w-5 h-5 text-rose-600"/>Foto de {photoDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-center">
              <div className="w-40 h-40 rounded-full bg-slate-100 border-4 border-slate-200 overflow-hidden flex items-center justify-center">
                {photoPreview ? (
                  <img src={photoPreview} alt="" className="w-full h-full object-cover" data-testid="user-photo-preview"/>
                ) : (
                  <Camera className="w-14 h-14 text-slate-300"/>
                )}
              </div>
            </div>
            <div className="flex items-center justify-center gap-2">
              <label className="inline-flex items-center gap-2 cursor-pointer bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 rounded-lg text-sm font-bold uppercase" data-testid="user-photo-upload">
                <Upload className="w-4 h-4"/>Escolher imagem
                <input type="file" accept="image/*" className="hidden" onChange={handlePhotoFile}/>
              </label>
              {photoDialog?.photo && (
                <Button variant="outline" onClick={removePhoto} disabled={photoSaving} data-testid="user-photo-remove"><X className="w-4 h-4 mr-1"/>Remover</Button>
              )}
            </div>
            <p className="text-xs text-slate-500 text-center">A foto aparecerá no ranking de avaliações e no card do funcionário no portal do aluno. Máx 3MB.</p>
            <div className="text-center text-xs">
              {photoDetecting ? (
                <span className="text-slate-500">Analisando rosto…</span>
              ) : photoPreview && photoFaceOk === true ? (
                <span className="text-emerald-700 inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded px-2 py-1" data-testid="face-ok-badge"><ScanFace className="w-3 h-3"/>Rosto detectado — o funcionário vai passar na catraca após salvar</span>
              ) : photoPreview && photoFaceOk === false ? (
                <span className="text-amber-700 inline-flex items-center gap-1 bg-amber-50 border border-amber-200 rounded px-2 py-1">⚠️ Rosto não detectado — foto será salva, mas sem acesso facial</span>
              ) : null}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={()=>{setPhotoDialog(null); setPhotoPreview("");}}>Cancelar</Button>
              <Button onClick={savePhoto} disabled={photoSaving || !photoPreview} className="bg-emerald-600 hover:bg-emerald-700 font-bold uppercase" data-testid="user-photo-save">{photoSaving ? "Salvando..." : "Salvar foto"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!permDialog} onOpenChange={(v)=>{if(!v) setPermDialog(null);}}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Permissões — {permDialog?.name}</DialogTitle></DialogHeader>
          {permDialog?.is_owner ? (
            <p className="text-sm text-slate-500 py-4" data-testid="user-perms-owner-note">Este é o administrador dono do sistema e sempre tem acesso a tudo.</p>
          ) : (
            <>
              <p className="text-xs text-slate-500 mb-2" data-testid="user-perms-hint">{permDialog?.role === "admin"
                ? "Administrador: se nada estiver marcado, ele vê todas as telas. Marque para mostrar apenas as telas escolhidas (ele continua com poderes de administrador nelas)."
                : `Se nada estiver marcado, o usuário verá o padrão do perfil (${roleLabel(permDialog?.role)}). Marque para permitir explicitamente cada módulo.`}</p>
              <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="user-perms-list">
                {MODULES.map(m => (
                  <label key={m.key} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded cursor-pointer">
                    <Checkbox checked={editingPerms.includes(m.key)} onCheckedChange={()=>togglePerm(m.key)} data-testid={`perm-${m.key}`}/>
                    <span className="text-sm">{m.label}</span>
                    <span className="ml-auto text-xs font-mono text-slate-400">{m.key}</span>
                  </label>
                ))}
              </div>
              <Button onClick={savePerms} data-testid="user-perms-save-button" className="w-full bg-rose-600 hover:bg-rose-700 mt-4">Salvar permissões</Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
